/**
 * This is a NotifyMe component.This component will used to notify the user through email when the product is available.
 *
 * @module views/components/NotifyMeV2
 * @memberof -Common
 */
import './NotifyMeV2.scss';

import React, { useCallback } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import InputField_New from '@ulta/core/components/InputField_New/InputField_New';
import Text from '@ulta/core/components/Text/Text';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { addYupCustomRegex } from '@ulta/utils/Validations/Validations';

import ProductBookmark from '../ProductBookmark/ProductBookmark';
import * as utils from './NotifyMeV2';

/**
 * Represents a NotifyMe component
 *
 * @method
 * @param {NotifyMeV2Props} props - React properties passed from composition
 * @returns NotifyMeV2
 */
export const NotifyMeV2 = function( props ){
  const {
    email,
    notifyMeMessage,
    notifyMeAction,
    notifyMeDisabled,
    invokeMutation
  } = props;

  const onSubmit = useCallback(
    utils.composeOnSubmit( { props }, { invokeMutation } ),
    [props, invokeMutation]
  );

  if( !notifyMeAction ){
    return null;
  }

  return (
    <div className='NotifyMe'>
      <Formik
        initialValues={ {
          emailAddress: ''
        } }
        validationSchema={ utils.getValidationSchema( { email } ) }
        onSubmit={ onSubmit }
      >
        <Form>
          <Text
            htmlTag='p'
            textStyle='body-2'
          >
            { notifyMeMessage }
          </Text>
          <InputField_New
            name={ email.name }
            type='email'
            label={ email.label }
            autoComplete='email'
            autoCorrect='on'
            spellCheck={ true }
            showValidationMessage={ true }
            showValueText=''
            hideValueText=''
            disabled={ notifyMeDisabled }
            newValue={ ' ' }
          />
          <div className='NotifyMe__bookMark'>
            <Button
              type='submit'
              label={ notifyMeAction.label }
              disabled={ notifyMeDisabled }
              action={ notifyMeAction }
            />
            <ProductBookmark
              id={ props.id }
              invokeMutation={ props.invokeMutation }
              bookmarkAction={ props.bookmarkAction }
              bookmarked={ props.bookmarked }
              commerceId={ props.commerceId }
              key='ProductBookmark'
              productActionsProps={ props }
              productId={ props.productId }
              quantity={ props.quantity }
              removeBookmarkAction={ props.removeBookmarkAction }
              skuId={ props.skuId }
              state={ props.state }
            />
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export const composeOnSubmit = ( data, methods ) => ( values ) => {
  const { emailAddress } = values || {};
  const { props } = data;
  const { notifyMeAction } = props || {};
  const { graphql, customHeaders, params } = notifyMeAction || {};
  const { invokeMutation } = methods || {};
  if( !emailAddress || !graphql || !invokeMutation ){
    return ;
  }
  invokeMutation( {
    graphql,
    customHeaders,
    params,
    variables: {
      moduleParams: { notifyEmailAddress: emailAddress },
      url: { path: !isServer() && global.location.pathname } }
  } );
};

/**
 * A handler function for manually setting form fields
 * @method getValidationSchema
 * @param {object} data - Contains input fields and their Amplience configurations
 * @returns {object} validationSchema - Returns Object containing Yup validations for each passed in field.
 */
export const getValidationSchema = ( data ) => {
  const { email } = handleEmptyObjects( data );

  addYupCustomRegex( { schemaType: Yup.string, name: 'emailAddress', validations: email?.validations } );

  return Yup.object( {
    emailAddress: email?.validations?.[0] && Yup.string().required( email.validations[0].message ).email( email.validations[0].message )
  } );
};

/**
 * Property type definitions
 *
 * @typedef NotifyMeV2Props
 * @type {object}
 * @property {object} email - Email input field to be displayed
 * @property {string} notifyMeMessage - Label to be displayed
 * @property {object} notifyMeAction - Label to be displayed for button
 * @property {boolean} notifyMeDisabled - Flag for the disabled notify me
 * @property {boolean} bookmarked - True/False value for the bookmarked
 * @property {object} bookmarkAction - Label to be displayed
 * @property {object} removeBookmarkAction - The action that should be executed when user removes a product from bookmarks consisting of Label and Graphql
 * @property {string} skuId - Sets skuId value
 * @property {string} productId - Sets productId value
 *
 */
export const propTypes = {
  email: PropTypes.object,
  notifyMeMessage: PropTypes.string,
  notifyMeAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  notifyMeDisabled: PropTypes.bool,
  bookmarked: PropTypes.bool,
  bookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeBookmarkAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  skuId: PropTypes.string,
  productId: PropTypes.string
};

NotifyMeV2.propTypes = propTypes;

export default NotifyMeV2;
